import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { getMethodLabel, COMMON_AUTHENTICATOR_APPS } from '../../constants/mfa';

const Profile = () => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [setupDialog, setSetupDialog] = useState({
    open: false,
    method: null,
    setup: null,
    code: '',
    appAttestation: false,
    sourceAppLabel: '',
    setupStep: 'scan_qr',
    materialLoading: false
  });
  const [verifyLoading, setVerifyLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    let mfaLoadError = null;

    try {
      await refreshProfile();
    } catch (err) {
      console.error('Refresh profile failed:', err);
    }

    try {
      const response = await api.get('/auth/mfa/status');
      setMfaStatus(response.data.data);
    } catch (err) {
      mfaLoadError = err;
      console.error('Load MFA status failed:', err);
    }

    if (mfaLoadError) {
      const message = mfaLoadError.response?.data?.message
        || (mfaLoadError.message === 'Network Error'
          ? 'Cannot reach the API. Run ./scripts/port-forward.sh and try again.'
          : 'Failed to load MFA status');
      toast.error(message);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const isEnrolled = (methodKey) =>
    mfaStatus?.enrollments?.some((row) => row.method === methodKey && row.is_verified);

  const resetSetupDialog = () => ({
    open: false,
    method: null,
    setup: null,
    code: '',
    appAttestation: false,
    sourceAppLabel: '',
    setupStep: 'scan_qr',
    materialLoading: false
  });

  const startTotpSetup = async (method) => {
    try {
      const response = await api.post('/auth/mfa/totp/setup', { method });
      const setup = response.data.data;
      setSetupDialog({
        open: true,
        method,
        setup,
        code: '',
        appAttestation: false,
        sourceAppLabel: '',
        setupStep: setup.strictAppBinding ? 'confirm_app' : 'scan_qr',
        materialLoading: false
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start authenticator setup');
    }
  };

  const confirmStrictApp = async () => {
    if (!setupDialog.sourceAppLabel) {
      toast.error('Select which authenticator app you will use.');
      return;
    }

    setSetupDialog((prev) => ({ ...prev, materialLoading: true }));
    try {
      const response = await api.post('/auth/mfa/totp/setup/material', {
        setupId: setupDialog.setup.setupId,
        method: setupDialog.method,
        sourceAppLabel: setupDialog.sourceAppLabel
      });
      setSetupDialog((prev) => ({
        ...prev,
        materialLoading: false,
        setupStep: 'scan_qr',
        setup: {
          ...prev.setup,
          ...response.data.data
        }
      }));
    } catch (err) {
      setSetupDialog((prev) => ({ ...prev, materialLoading: false }));
      toast.error(err.response?.data?.message || 'This authenticator app is not allowed for this MFA method');
    }
  };

  const enableEmailOtp = async () => {
    try {
      await api.post('/auth/mfa/email/enable');
      toast.success('Email OTP enabled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to enable email OTP');
    }
  };

  const verifyTotpSetup = async () => {
    setVerifyLoading(true);
    try {
      await api.post('/auth/mfa/totp/verify', {
        setupId: setupDialog.setup.setupId,
        method: setupDialog.method,
        code: setupDialog.code,
        appAttestation: setupDialog.setup?.strictAppBinding ? setupDialog.appAttestation : true,
        sourceAppLabel: setupDialog.setup?.strictAppBinding ? setupDialog.sourceAppLabel : undefined
      });
      toast.success('Authenticator enrolled');
      setSetupDialog(resetSetupDialog());
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const disableMethod = async (method) => {
    try {
      await api.delete(`/auth/mfa/${method}`);
      toast.success(`${getMethodLabel(method, mfaStatus?.org_enabled_methods)} disabled`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disable MFA method');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const profile = user || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>My Profile</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Account Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">First Name</Typography><Typography>{profile.first_name || '-'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Last Name</Typography><Typography>{profile.last_name || '-'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Email</Typography><Typography>{profile.email || '-'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Role</Typography><Typography sx={{ textTransform: 'capitalize' }}>{profile.role?.replace('_', ' ') || '-'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Last Login</Typography><Typography>{profile.last_login ? new Date(profile.last_login).toLocaleString() : '-'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Status</Typography><Chip size="small" color={profile.is_active === false ? 'default' : 'success'} label={profile.is_active === false ? 'Inactive' : 'Active'} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Multi-factor Authentication</Typography>
        </Box>

        {mfaStatus === null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            MFA status could not be loaded. Check that port-forward is running and refresh.
          </Alert>
        )}

        {mfaStatus?.policy?.require_at_least_one_mfa && !mfaStatus?.meets_minimum_policy && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your administrator requires at least one MFA method. Please enroll below.
          </Alert>
        )}
        {mfaStatus?.policy?.step_up_authentication && !mfaStatus?.meets_step_up_policy && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Step-up authentication is enabled. Enroll at least two MFA methods.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enrolled methods: {mfaStatus?.verified_count || 0}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {(mfaStatus?.org_enabled_methods || []).map((method) => {
          const enrolled = isEnrolled(method.key);
          return (
            <Box
              key={method.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              <Box>
                <Typography fontWeight={600}>{method.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {method.type === 'email'
                    ? 'Email OTP'
                    : method.strict_app_binding
                      ? `${method.required_app_label || method.label} only (org policy)`
                      : 'Authenticator app'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {enrolled ? (
                  <>
                    <Chip size="small" color="success" label="Enabled" />
                    <Button size="small" color="error" onClick={() => disableMethod(method.key)}>Disable</Button>
                  </>
                ) : method.type === 'email' ? (
                  <Button size="small" variant="outlined" onClick={enableEmailOtp}>Enable</Button>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => startTotpSetup(method.key)}>Set up</Button>
                )}
              </Box>
            </Box>
          );
        })}

        {(!mfaStatus?.org_enabled_methods || mfaStatus.org_enabled_methods.length === 0) && (
          <Alert severity="info">No MFA methods are enabled by your administrator.</Alert>
        )}
      </Paper>

      <Dialog
        open={setupDialog.open}
        onClose={() => setSetupDialog(resetSetupDialog())}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set up {getMethodLabel(setupDialog.method, mfaStatus?.org_enabled_methods)}</DialogTitle>
        <DialogContent>
          {setupDialog.setup?.strictAppBinding && setupDialog.setupStep === 'confirm_app' && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                Strict app binding is enabled. You must use <strong>{setupDialog.setup.requiredAppLabel}</strong> for this
                method. Google Authenticator and other apps are not allowed here.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                If you want Google Authenticator, ask your administrator to enable the Google Authenticator MFA method
                and enroll that separately.
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="source-app-label">Which app will you use?</InputLabel>
                <Select
                  labelId="source-app-label"
                  label="Which app will you use?"
                  value={setupDialog.sourceAppLabel}
                  onChange={(e) => setSetupDialog((prev) => ({ ...prev, sourceAppLabel: e.target.value }))}
                >
                  {COMMON_AUTHENTICATOR_APPS.map((appLabel) => (
                    <MenuItem key={appLabel} value={appLabel}>{appLabel}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {setupDialog.setup?.strictAppBinding && setupDialog.setupStep !== 'confirm_app' && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Confirmed app: <strong>{setupDialog.sourceAppLabel}</strong>. Scan only with this app.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                1. Open <strong>{setupDialog.setup.requiredAppLabel}</strong>.
                <br />
                2. Add account → Scan QR code below.
                <br />
                3. Enter the 6-digit code from that same app.
              </Typography>
            </>
          )}

          {!setupDialog.setup?.strictAppBinding && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {setupDialog.setup?.setupInstructions
                || `Scan this QR code with ${setupDialog.setup?.requiredAppLabel || 'your authenticator app'}, then enter the 6-digit code.`}
            </Typography>
          )}

          {setupDialog.setup?.qrCodeDataUrl && setupDialog.setupStep !== 'confirm_app' && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img src={setupDialog.setup.qrCodeDataUrl} alt="Authenticator QR code" style={{ maxWidth: 220 }} />
            </Box>
          )}

          {!setupDialog.setup?.strictAppBinding && setupDialog.setup?.secret && (
            <Typography variant="caption" display="block" sx={{ mb: 2, wordBreak: 'break-all' }}>
              Manual key: {setupDialog.setup.secret}
            </Typography>
          )}

          {setupDialog.setupStep !== 'confirm_app' && (
            <>
              {setupDialog.setup?.strictAppBinding && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="verify-source-app-label">Which app provided this code?</InputLabel>
                  <Select
                    labelId="verify-source-app-label"
                    label="Which app provided this code?"
                    value={setupDialog.sourceAppLabel}
                    onChange={(e) => setSetupDialog((prev) => ({ ...prev, sourceAppLabel: e.target.value }))}
                  >
                    {COMMON_AUTHENTICATOR_APPS.map((appLabel) => (
                      <MenuItem key={appLabel} value={appLabel}>{appLabel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {setupDialog.setup?.strictAppBinding && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={setupDialog.appAttestation}
                      onChange={(e) => setSetupDialog((prev) => ({ ...prev, appAttestation: e.target.checked }))}
                    />
                  }
                  label={`I added this account only in ${setupDialog.setup.requiredAppLabel}`}
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
              )}
              <TextField
                fullWidth
                label="6-digit code"
                value={setupDialog.code}
                onChange={(e) => setSetupDialog((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialog(resetSetupDialog())}>Cancel</Button>
          {setupDialog.setupStep === 'confirm_app' ? (
            <Button
              variant="contained"
              onClick={confirmStrictApp}
              disabled={setupDialog.materialLoading || !setupDialog.sourceAppLabel}
            >
              {setupDialog.materialLoading ? 'Checking...' : 'Continue'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={verifyTotpSetup}
              disabled={
                verifyLoading
                || setupDialog.code.length !== 6
                || (setupDialog.setup?.strictAppBinding && (!setupDialog.appAttestation || !setupDialog.sourceAppLabel))
              }
            >
              Verify & Enable
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
